import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
    Image as ImageIcon,
    Video,
    Music,
    Download,
    Clock,
    Search,
    RefreshCw,
    ExternalLink,
    ChevronRight,
    ChevronLeft,
    Loader2,
    Trash2,
    LayoutGrid,
    List,
    AlertCircle,
    AlertTriangle,
    Repeat,
    X,
    Maximize2,
    Play,
    Crown,
    Copy,
    Stamp,
    Bot,
    Mic
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { checkTaskStatus } from "@/lib/task-checker";
import { useToast } from "@/hooks/use-toast";
import { useGeneration } from "@/hooks/useGeneration";

interface GenerationLog {
    id: string;
    service_type: "photo" | "video" | "music" | "video_motion" | "watermark" | "prompt_ai" | "speech";
    model: string;
    prompt: string;
    display_prompt?: string | null;
    is_professional?: boolean;
    is_magic?: boolean;
    credits_used: number;
    result_url: string | null;
    status: "pending" | "processing" | "completed" | "failed";
    created_at: string;
    completed_at: string | null;
    error_message?: string | null;
    auto_retry?: boolean;
    retry_count?: number;
    max_retries?: number;
    last_retry_at?: string | null;
    refunded_at?: string | null;
    refunded_credits?: number | null;
}

const NEW_BADGE_WINDOW_MS = 8 * 60 * 1000;

// Thai translation map for common error messages
const ERROR_TRANSLATIONS: Record<string, string> = {
    // OpenAI/Sora guardrails
    "This content may violate OpenAI's guardrails around violence.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับความรุนแรง",
    "This content may violate OpenAI's guardrails around nudity.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับภาพเปลือย",
    "This content may violate OpenAI's guardrails around sexual content.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับเนื้อหาทางเพศ",
    "This content may violate OpenAI's guardrails around hate speech.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับคำพูดแสดงความเกลียดชัง",
    "This content may violate OpenAI's guardrails around illegal activities.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI เกี่ยวกับกิจกรรมผิดกฎหมาย",
    "This content may violate OpenAI's guardrails.": "เนื้อหานี้อาจละเมิดกฎความปลอดภัยของ OpenAI",
    // Generic errors
    "Generation failed": "การสร้างล้มเหลว",
    "Task failed at submission": "งานล้มเหลวตอนส่ง",
    "No task ID found - task failed at submission": "ไม่พบ Task ID - งานล้มเหลวตอนส่ง",
    "Task failed at submission - no task ID was created": "งานล้มเหลวตอนส่ง - ไม่มี Task ID ถูกสร้าง",
    "Request timeout": "หมดเวลาคำขอ",
    "Request timeout - task took too long to complete": "หมดเวลาคำขอ - งานใช้เวลานานเกินไป",
    "Internal server error": "ข้อผิดพลาดภายในเซิร์ฟเวอร์",
    "Rate limit exceeded": "เกินอัตราการใช้งานที่กำหนด",
    "Invalid input": "ข้อมูลไม่ถูกต้อง",
    "Invalid prompt": "พรอมต์ไม่ถูกต้อง",
    // Content policy
    "Content policy violation": "ละเมิดนโยบายเนื้อหา",
    "Prompt contains prohibited content": "พรอมต์มีเนื้อหาที่ห้าม",
    // Real person / Celebrity
    "Real person detected": "ตรวจพบคนจริง - ห้ามใช้คนจริง",
    "Celebrity likeness detected": "ตรวจพบใบหน้าคนดัง - ห้ามใช้ภาพคนดัง",
    // Character specific
    "Character generation failed": "การสร้างตัวละครล้มเหลว",
    "Invalid character video": "วิดีโอตัวละครไม่ถูกต้อง",
    "The video of creating character fetch failed. Check access settings or use our File Upload API instead.": "ดึงวิดีโอตัวละครไม่สำเร็จ - กรุณาตรวจสอบวิดีโอที่ใช้สร้างตัวละคร หรือลองอัปโหลดใหม่",
    // Server/Service errors
    "Sora official service is currently under heavy load and not responding. Please try again later.": "เซิร์ฟเวอร์ Sora มีโหลดสูง กรุณาลองใหม่ภายหลัง",
};

// Function to translate error message
const translateErrorMessage = (errorMessage: string, isThaiMode: boolean): string => {
    if (!isThaiMode) return errorMessage;

    // Check exact match first
    if (ERROR_TRANSLATIONS[errorMessage]) {
        return ERROR_TRANSLATIONS[errorMessage];
    }

    // Check partial match for OpenAI guardrails
    for (const [eng, thai] of Object.entries(ERROR_TRANSLATIONS)) {
        if (errorMessage.toLowerCase().includes(eng.toLowerCase())) {
            return thai;
        }
    }

    // Check for keywords and provide generic translations
    const lowered = errorMessage.toLowerCase();
    if (lowered.includes("guardrail") || lowered.includes("policy") || lowered.includes("violat")) {
        return `ละเมิดกฎความปลอดภัย: ${errorMessage}`;
    }
    if (lowered.includes("violence") || lowered.includes("violent")) {
        return `เนื้อหามีความรุนแรง: ${errorMessage}`;
    }
    if (lowered.includes("nudity") || lowered.includes("naked") || lowered.includes("sexual")) {
        return `เนื้อหาไม่เหมาะสม: ${errorMessage}`;
    }
    if (lowered.includes("real person") || lowered.includes("celebrity") || lowered.includes("likeness")) {
        return `ห้ามใช้คนจริง/คนดัง: ${errorMessage}`;
    }

    return errorMessage;
};

const History = () => {
    const { t } = useLanguage();
    const { user, loading: authLoading, refreshProfile } = useAuth();
    const navigate = useNavigate();
    const { type: typeParam } = useParams();
    const { toast: showToast } = useToast();
    const { refundCredits } = useGeneration();
    const [logs, setLogs] = useState<GenerationLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const defaultTab = typeParam || "all";
    const [activeTab, setActiveTab] = useState<string>(defaultTab);
    const [searchQuery, setSearchQuery] = useState("");
    const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
    const [isCheckingAll, setIsCheckingAll] = useState(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [selectedErrorLog, setSelectedErrorLog] = useState<GenerationLog | null>(null);
    const [now, setNow] = useState(Date.now());

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const ITEMS_PER_PAGE = 30;
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

    // Update tab when URL changes
    useEffect(() => {
        if (typeParam && typeParam !== activeTab) {
            setActiveTab(typeParam);
            setCurrentPage(1); // Reset page on tab change
        }
    }, [typeParam]);

    const handleTabChange = (val: string) => {
        setActiveTab(val);
        setCurrentPage(1); // Reset page on tab change
        if (val === "all") {
            navigate("/history", { replace: true });
        } else {
            navigate(`/history/${val}`, { replace: true });
        }
    };

    const fetchLogs = async () => {
        if (!user) return;

        setIsLoading(true);
        try {
            // Get count first (exclude soft-deleted items)
            let countQuery = supabase
                .from("generation_logs")
                .select("id", { count: "exact", head: true })
                .eq("user_id", user.id)
                .or("is_deleted.is.null,is_deleted.eq.false");

            if (activeTab !== "all") {
                if (activeTab === "motion") {
                    countQuery = countQuery.eq("service_type", "video_motion" as any);
                } else if (activeTab === "prompt_ai") {
                    countQuery = countQuery.eq("service_type", "prompt_ai" as any);
                } else {
                    countQuery = countQuery.eq("service_type", activeTab as any);
                }
            }

            const { count, error: countError } = await countQuery;
            if (countError) throw countError;

            setTotalPages(Math.ceil((count || 0) / ITEMS_PER_PAGE));

            // Get data for current page (exclude soft-deleted items)
            let query = supabase
                .from("generation_logs")
                .select("*")
                .eq("user_id", user.id)
                .or("is_deleted.is.null,is_deleted.eq.false")
                .order("created_at", { ascending: false })
                .range((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE - 1);

            if (activeTab !== "all") {
                if (activeTab === "motion") {
                    query = query.eq("service_type", "video_motion" as any);
                } else if (activeTab === "prompt_ai") {
                    query = query.eq("service_type", "prompt_ai" as any);
                } else {
                    query = query.eq("service_type", activeTab as any);
                }
            }

            const { data, error } = await query;

            if (error) throw error;
            setLogs(data as GenerationLog[]);
        } catch (error: any) {
            console.error("Error fetching logs:", error);
            showToast({
                title: t("ไม่สามารถโหลดประวัติได้", "Failed to load history"),
                description: error.message,
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading && !user) {
            navigate("/login");
        } else if (user) {
            fetchLogs();
        }
    }, [user, authLoading, activeTab, currentPage]);

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 60000);

        return () => clearInterval(interval);
    }, []);

    // Helper function to get the prompt to display (hide full prompt for professional mode)
    const getDisplayPrompt = (log: GenerationLog): string => {
        if (log.is_magic) {
            return log.display_prompt || t("Magic Button", "Magic Button");
        }
        // If it's professional mode or the model name contains "Professional",
        // we MUST hide the full prompt and only show the display_prompt (optional part)
        const isProfessional = log.is_professional || log.model.includes("Professional");
        return isProfessional && log.display_prompt ? log.display_prompt : log.prompt;
    };

    // Helper to get all downloadable URLs and their preferred filenames
    const getDownloadItems = (log: GenerationLog) => {
        if (!log.result_url) return [];

        let items: { url: string; title?: string }[] = [];

        // If it's JSON
        if (log.result_url.startsWith('{')) {
            try {
                const parsed = JSON.parse(log.result_url);

                // Format 1: tracks (Music)
                if (parsed.tracks && Array.isArray(parsed.tracks)) {
                    items = parsed.tracks.map((t: any) => ({
                        url: t.audio || t.url || t.video_url,
                        title: t.title
                    }));
                }
                // Format 2: resultUrls (Video)
                else if (parsed.resultUrls && Array.isArray(parsed.resultUrls)) {
                    items = parsed.resultUrls.map((u: string) => ({ url: u }));
                }
                // Format 3: url (Single result)
                else if (parsed.url) {
                    items = [{ url: parsed.url }];
                }
            } catch (e) {
                // Fall back to plain string string handling below
            }
        }

        // If items are still empty, try parsing as plain string (possibly comma-separated)
        if (items.length === 0) {
            items = log.result_url.split(',')
                .map(u => u.trim())
                .filter(u => u.startsWith('http'))
                .map(u => ({ url: u }));
        }

        // Add extensions and default titles
        return items.filter(item => item.url).map((item, idx) => {
            let url = item.url;
            // Fix Kie.ai music stream URLs
            if (url.includes('musicfile.kie.ai') && !url.includes('.mp3')) {
                url = url + '.mp3';
            }

            // Determine extension
            let ext = 'file';
            if (url.toLowerCase().includes('.mp3')) ext = 'mp3';
            else if (url.toLowerCase().includes('.mp4')) ext = 'mp4';
            else if (url.toLowerCase().includes('.png')) ext = 'png';
            else if (url.toLowerCase().includes('.jpg') || url.toLowerCase().includes('.jpeg')) ext = 'jpg';
            else {
                // Default based on service type
                if (log.service_type === 'music') ext = 'mp3';
                else if (log.service_type === 'video' || log.service_type === 'video_motion') ext = 'mp4';
                else if (log.service_type === 'photo') ext = 'png';
            }

            return {
                url,
                title: item.title || `${log.service_type}_${idx + 1}`,
                filename: `${item.title || log.service_type}_${idx + 1}.${ext}`
            };
        });
    };

    const handleDownloadFile = async (url: string, filename: string) => {
        try {
            const response = await fetch(url, { mode: 'cors' });
            if (!response.ok) throw new Error('Fetch failed');
            const blob = await response.blob();
            if (blob.size === 0) throw new Error('Empty blob');
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Download failed, opening in new tab:", err);
            window.open(url, '_blank');
        }
    };

    const filteredLogs = logs.filter(log => {
        const displayPrompt = getDisplayPrompt(log);
        return displayPrompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
            log.model.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const getStatusBadge = (status: string, log?: GenerationLog) => {
        const maxRetries = log?.max_retries || 20;
        const retryCount = log?.retry_count || 0;

        // Show retry progress badge for auto_retry jobs
        const getRetryBadge = () => {
            if (!log?.auto_retry) return null;

            if (status === "completed" && retryCount > 0) {
                // Completed after retries
                return (
                    <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                        {t(`สำเร็จหลังลอง ${retryCount} ครั้ง`, `Success after ${retryCount} retries`)}
                    </Badge>
                );
            }

            if (status === "failed" && retryCount < maxRetries) {
                // Failed but will retry
                return (
                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 animate-pulse">
                        {t(`รอลองซ้ำ ${retryCount + 1}/${maxRetries}`, `Retry ${retryCount + 1}/${maxRetries}`)}
                    </Badge>
                );
            }

            if (status === "failed" && retryCount >= maxRetries) {
                // Max retries exceeded
                return (
                    <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                        {t(`หมด ${maxRetries} ครั้ง`, `${maxRetries} retries exhausted`)}
                    </Badge>
                );
            }

            if (retryCount > 0) {
                return (
                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                        {t(`ลองครั้งที่ ${retryCount + 1}`, `Attempt ${retryCount + 1}`)}
                    </Badge>
                );
            }

            return (
                <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                    {t("เจนซ้ำอัตโนมัติ", "Auto Retry")}
                </Badge>
            );
        };

        switch (status) {
            case "completed":
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-green-500/10 text-green-500 border-green-500/20 hover:bg-green-500/20">{t("สำเร็จ", "Completed")}</Badge>
                        {log?.auto_retry && retryCount > 0 && getRetryBadge()}
                    </div>
                );
            case "processing":
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 animate-pulse">{t("กำลังประมวลผล", "Processing")}</Badge>
                        {log?.auto_retry && getRetryBadge()}
                    </div>
                );
            case "pending":
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">{t("รอดำเนินการ", "Pending")}</Badge>
                        {log?.auto_retry && getRetryBadge()}
                    </div>
                );
            case "failed":
                return (
                    <div className="flex items-center gap-1 flex-wrap">
                        <Badge
                            variant="destructive"
                            className="bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (log) setSelectedErrorLog(log);
                            }}
                        >
                            {t("ล้มเหลว", "Failed")}
                        </Badge>
                        {log?.refunded_at && (
                            <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px] h-5">
                                {t("คืนแล้ว", "Refunded")}
                            </Badge>
                        )}
                        {log?.auto_retry && getRetryBadge()}
                    </div>
                );
            default:
                return null;
        }
    };

    const getServiceIcon = (type: string) => {
        switch (type) {
            case "photo": return <ImageIcon className="h-4 w-4" />;
            case "video": return <Video className="h-4 w-4" />;
            case "music": return <Music className="h-4 w-4" />;
            case "video_motion": return <Play className="h-4 w-4" />;
            case "watermark": return <Stamp className="h-4 w-4" />;
            case "prompt_ai": return <Bot className="h-4 w-4" />;
            case "speech": return <Mic className="h-4 w-4" />;
            default: return null;
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('th-TH', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    // Helper to get first video URL from log
    const getFirstVideoUrl = (log: GenerationLog): string | null => {
        if (!log.result_url) return null;

        let videoUrl = log.result_url;
        if (videoUrl.startsWith('{')) {
            try {
                const parsed = JSON.parse(videoUrl);
                videoUrl = parsed.resultUrls?.[0] || parsed.url || videoUrl;
            } catch { }
        } else if (videoUrl.includes(',')) {
            videoUrl = videoUrl.split(',')[0].trim();
        }

        return videoUrl.startsWith('http') ? videoUrl : null;
    };

    const copyVideoUrl = (log: GenerationLog) => {
        const url = getFirstVideoUrl(log);
        if (url) {
            navigator.clipboard.writeText(url);
            toast.success(t("คัดลอก URL แล้ว", "URL copied"));
        }
    };

    const goToWatermark = (log: GenerationLog) => {
        const url = getFirstVideoUrl(log);
        if (url) {
            navigate("/watermark", { state: { videoUrl: url } });
        }
    };

    const handleReuse = (log: GenerationLog) => {
        if (log.is_magic) {
            showToast({
                title: t("พรอมต์ถูกซ่อน", "Prompt hidden"),
                description: t("ไม่สามารถใช้พรอมต์นี้ซ้ำได้", "This prompt cannot be reused"),
            });
            return;
        }
        // Use display_prompt if available (for professional mode), otherwise use full prompt
        const promptToReuse = getDisplayPrompt(log);

        // Handle Sora models - navigate to specific Sora tab
        if (log.model.includes("sora")) {
            let soraTab = "text-to-video";
            if (log.model.includes("image-to-video")) {
                soraTab = "image-to-video";
            } else if (log.model.includes("characters")) {
                soraTab = "characters";
            }
            navigate(`/generate/sora/${soraTab}`, {
                state: { prompt: promptToReuse }
            });
            return;
        }

        // Handle speech service type
        if (log.service_type === "speech" || log.model.includes("elevenlabs")) {
            navigate("/generate/text-to-speech", {
                state: { prompt: promptToReuse }
            });
            return;
        }

        navigate(`/generate/${log.service_type}`, {
            state: { prompt: promptToReuse }
        });
    };

    const handleDelete = async (id: string) => {
        try {
            // Find log info for refund logic before deletion
            const logToDelete = logs.find(l => l.id === id);

            // Try delete via RPC (Secure function handling Owner & Admin)
            const { data: success, error } = await supabase.rpc('delete_generation_log' as any, { p_log_id: id });

            if (error) {
                console.warn("RPC delete failed, falling back to standard delete...", error);
                // Fallback: Standard delete
                const { data: deletedData, error: dbError } = await supabase
                    .from("generation_logs")
                    .delete()
                    .eq("id", id)
                    .select();

                if (dbError) throw dbError;
                if (!deletedData || deletedData.length === 0) throw new Error("Delete failed: Permission denied or Not found");
            } else if (!success) {
                throw new Error("Permission denied: You cannot delete this log.");
            }

            // Update UI
            setLogs(logs.filter(log => log.id !== id));

            // Refund credits if status was failed
            if (logToDelete && logToDelete.status === "failed" && logToDelete.credits_used > 0) {
                const refunded = await refundCredits(logToDelete.id);
                if (refunded) {
                    toast.success(t(`คืน ${logToDelete.credits_used} เครดิตแล้ว`, `Refunded ${logToDelete.credits_used} credits`));
                }
            }

            toast.success(t("ลบประวัติแล้ว", "History deleted"));
        } catch (error: any) {
            console.error("Delete error:", error);
            toast.error(t("เกิดข้อผิดพลาดในการลบ", "Error deleting history") + ": " + (error.message || ""));
        }
    };

    const handleCheckStatus = async (logId: string) => {
        setCheckingStatus(logId);
        try {
            const result = await checkTaskStatus(logId);

            if (result.success && result.status === "completed" && result.url) {
                showToast({
                    title: t("✅ เสร็จแล้ว!", "✅ Completed!"),
                    description: t("ผลลัพธ์พร้อมแล้ว", "Result is ready")
                });
                // Reload data
                fetchLogs();
            } else if (result.status === "processing") {
                showToast({
                    title: t("⏳ กำลังประมวลผล", "⏳ Processing"),
                    description: t("กรุณารอสักครู่", "Please wait a moment")
                });
            } else if (result.status === "failed") {
                // Auto-attempt refund for failed jobs
                try {
                    await supabase.rpc('refund_credits_for_failed_generation' as any, { p_log_id: logId });
                } catch (refundErr) {
                    console.error("Auto-refund failed:", refundErr);
                }

                showToast({
                    title: t("❌ ล้มเหลว", "❌ Failed"),
                    description: result.message,
                    variant: "destructive"
                });
                // Reload data after failure to update UI
                fetchLogs();
                refreshProfile(); // Refresh credits in UI
            }
        } catch (error: any) {
            showToast({
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: error.message,
                variant: "destructive"
            });
        } finally {
            setCheckingStatus(null);
        }
    };

    const handleCheckAllStatus = async () => {
        // Get all processing items from current logs
        const processingLogs = logs.filter(log => log.status === "processing" || log.status === "pending");

        if (processingLogs.length === 0) {
            showToast({
                title: t("ไม่มีงานที่รอตรวจสอบ", "No pending jobs"),
                description: t("ไม่พบงานที่กำลังประมวลผล", "No processing jobs found")
            });
            return;
        }

        setIsCheckingAll(true);
        let completedCount = 0;
        let failedCount = 0;

        showToast({
            title: t(`กำลังตรวจสอบ ${processingLogs.length} รายการ...`, `Checking ${processingLogs.length} items...`)
        });

        for (const log of processingLogs) {
            try {
                const result = await checkTaskStatus(log.id);
                if (result.success && result.status === "completed") {
                    completedCount++;
                } else if (result.status === "failed") {
                    failedCount++;
                }
                // Small delay between checks to avoid rate limiting
                await new Promise(resolve => setTimeout(resolve, 300));
            } catch (error) {
                console.error(`Error checking status for ${log.id}:`, error);
            }
        }

        // Reload logs after checking all
        await fetchLogs();
        setIsCheckingAll(false);

        showToast({
            title: t("ตรวจสอบเสร็จสิ้น", "Check completed"),
            description: t(
                `เสร็จ ${completedCount} | ล้มเหลว ${failedCount} | รอดำเนินการ ${processingLogs.length - completedCount - failedCount}`,
                `Done ${completedCount} | Failed ${failedCount} | Pending ${processingLogs.length - completedCount - failedCount}`
            )
        });
    };

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <Navbar />
            <main className="flex-1 pt-24 pb-16">
                <div className="container mx-auto px-4">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
                        <div className="animate-in fade-in slide-in-from-left duration-500">
                            <h1 className="text-4xl font-bold mb-2">
                                {t("ประวัติการ", "Generation")} <span className="text-gradient">{t("สร้างผลงาน", "History")}</span>
                            </h1>
                            <p className="text-muted-foreground">
                                {t("รายการผลงานทั้งหมดที่คุณเคยสร้างด้วย AI", "View all the creative content you've generated with AI")}
                            </p>
                            <p className="text-sm text-yellow-500/90 mt-2 flex items-center gap-2 font-medium">
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                {t("กรุณาโหลดไฟล์เก็บไว้ เพราะว่า ระบบจะ ลบทุกไฟล์ ภายใน 14 วัน", "Please download and save your files; the system will delete all files within 14 days.")}
                            </p>
                        </div>
                        <div className="flex items-center gap-3 animate-in fade-in slide-in-from-right duration-500">
                            <div className="flex bg-white/5 border border-white/10 rounded-xl p-1">
                                <Button
                                    variant={viewMode === "grid" ? "secondary" : "ghost"}
                                    size="icon"
                                    className={`h-9 w-9 rounded-lg ${viewMode === "grid" ? "bg-white/10" : "hover:bg-white/5"}`}
                                    onClick={() => setViewMode("grid")}
                                >
                                    <LayoutGrid className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant={viewMode === "list" ? "secondary" : "ghost"}
                                    size="icon"
                                    className={`h-9 w-9 rounded-lg ${viewMode === "list" ? "bg-white/10" : "hover:bg-white/5"}`}
                                    onClick={() => setViewMode("list")}
                                >
                                    <List className="h-4 w-4" />
                                </Button>
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="rounded-xl border-white/10 gap-2"
                                onClick={handleCheckAllStatus}
                                disabled={isCheckingAll || isLoading}
                            >
                                <RefreshCw className={`h-4 w-4 ${isCheckingAll ? "animate-spin" : ""}`} />
                                {t("เช็กทั้งหมด", "Check All")}
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                className="rounded-xl border-white/10"
                                onClick={fetchLogs}
                                disabled={isLoading}
                            >
                                <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                            </Button>
                            <div className="relative group min-w-[240px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                <input
                                    type="text"
                                    placeholder={t("ค้นหาจาก Prompt...", "Search prompts...")}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 h-11 bg-white/5 border border-white/10 rounded-xl focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Filters */}
                    <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-8">
                        <TabsList className="bg-white/5 border border-white/10 p-1 rounded-2xl flex-wrap">
                            <TabsTrigger value="all" className="rounded-xl px-6">{t("ทั้งหมด", "All")}</TabsTrigger>
                            <TabsTrigger value="photo" className="rounded-xl px-6 gap-2"><ImageIcon className="h-4 w-4" />{t("ภาพ", "Photos")}</TabsTrigger>
                            <TabsTrigger value="video" className="rounded-xl px-6 gap-2"><Video className="h-4 w-4" />{t("วิดีโอ", "Videos")}</TabsTrigger>
                            <TabsTrigger value="motion" className="rounded-xl px-6 gap-2"><Play className="h-4 w-4" />Motion</TabsTrigger>
                            <TabsTrigger value="music" className="rounded-xl px-6 gap-2"><Music className="h-4 w-4" />{t("เพลง", "Music")}</TabsTrigger>
                            <TabsTrigger value="prompt_ai" className="rounded-xl px-6 gap-2"><Bot className="h-4 w-4" />Prompt AI</TabsTrigger>
                            <TabsTrigger value="speech" className="rounded-xl px-6 gap-2"><Mic className="h-4 w-4" />{t("เสียงพูด", "Speech")}</TabsTrigger>
                            <TabsTrigger value="watermark" className="rounded-xl px-6 gap-2"><Stamp className="h-4 w-4" />{t("ลายน้ำ", "Watermark")}</TabsTrigger>
                        </TabsList>
                    </Tabs>

                    {/* History Content */}
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <Loader2 className="h-10 w-10 animate-spin text-primary" />
                            <p className="text-muted-foreground animate-pulse">{t("กำลังโหลดประวัติ...", "Loading history...")}</p>
                        </div>
                    ) : filteredLogs.length === 0 ? (
                        <Card variant="glass" className="border-white/10 py-20 text-center">
                            <CardContent>
                                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <AlertCircle className="h-8 w-8 text-primary" />
                                </div>
                                <h3 className="text-xl font-bold mb-2">{t("ไม่พบประวัติการสร้าง", "No history found")}</h3>
                                <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                                    {searchQuery
                                        ? t("ไม่พบผลลัพธ์ที่ตรงกับการค้นหาของคุณ", "No results match your search query")
                                        : t("คุณยังไม่ได้เริ่มสร้างผลงานใดๆ ลองเริ่มสร้างตอนนี้เลย!", "You haven't generated anything yet. Start creating now!")
                                    }
                                </p>
                                {!searchQuery && (
                                    <Button variant="glow" onClick={() => navigate("/generate")}>
                                        {t("เริ่มสร้างผลงาน", "Start Generating")}
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "flex flex-col gap-3"}>
                            {filteredLogs.map((log) => {
                                // Determine various states
                                const hasJsonResult = log.result_url?.startsWith("{");
                                const hasHttpResult = log.result_url?.startsWith("http");
                                const hasResult = hasJsonResult || hasHttpResult;
                                // Allow check status for video, music, and video_motion when status is pending/processing
                                const isPendingOrProcessing = log.status === "pending" || log.status === "processing";
                                const isFailed = log.status === "failed";
                                const canCheckStatus = (log.service_type === "video" || log.service_type === "music" || log.service_type === "video_motion" || log.service_type === "photo")
                                    && (isPendingOrProcessing || (isFailed && !log.refunded_at));
                                const isCompleted = (log.status === "completed" && hasResult);
                                const isPending = !isCompleted && !isFailed;
                                const badgeTimestamp = log.completed_at || log.created_at;
                                const badgeTimestampMs = new Date(badgeTimestamp).getTime();
                                const isNew = hasResult && !Number.isNaN(badgeTimestampMs) && (now - badgeTimestampMs <= NEW_BADGE_WINDOW_MS);

                                if (viewMode === "list") {
                                    return (
                                        <Card key={log.id} variant="glass" className="flex flex-col md:flex-row md:items-center p-4 gap-4 border-white/10 hover:border-primary/30 transition-all">
                                            <div className="flex items-center gap-4 flex-1 min-w-0">
                                                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0 text-primary">
                                                    {getServiceIcon(log.service_type)}
                                                </div>

                                                <div className="min-w-0 flex-1">
                                                    <p className="font-medium truncate text-sm text-foreground/90">{getDisplayPrompt(log)}</p>
                                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(log.created_at)}</span>
                                                        <span className="w-1 h-1 rounded-full bg-white/20"></span>
                                                        <span>{log.model}</span>
                                                        <span className="w-1 h-1 rounded-full bg-white/20"></span>
                                                        {log.status === "failed" ? (
                                                            log.refunded_at ? (
                                                                <span className="text-green-500">0 {t("เครดิต", "Credits")} ({t("คืนแล้ว", "Refunded")})</span>
                                                            ) : (
                                                                <span className="text-red-400">-{log.credits_used} {t("เครดิต", "Credits")} ({t("ยังไม่ได้คืน", "Not Refunded")})</span>
                                                            )
                                                        ) : (
                                                            <span className="text-primary/70">-{log.credits_used} {t("เครดิต", "Credits")}</span>
                                                        )}
                                                    </div>
                                                    {/* Error Message for Failed Jobs */}
                                                    {log.status === 'failed' && log.error_message && (
                                                        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-red-400">
                                                            <AlertTriangle className="h-3 w-3 shrink-0" />
                                                            <span className="truncate" title={log.error_message}>
                                                                {translateErrorMessage(log.error_message, t("th", "en") === "th")}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between md:justify-end gap-4 w-full md:w-auto mt-2 md:mt-0 pt-2 md:pt-0 border-t md:border-t-0 border-white/5">
                                                <div className="shrink-0 flex items-center gap-2">
                                                    {isNew && (
                                                        <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">
                                                            New
                                                        </Badge>
                                                    )}
                                                    {getStatusBadge(log.status, log)}
                                                </div>

                                                <div className="flex items-center gap-1 shrink-0">
                                                    {log.service_type !== "watermark" && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                            onClick={() => handleReuse(log)}
                                                            disabled={log.is_magic}
                                                            title={log.is_magic ? t("พรอมต์ถูกซ่อน", "Prompt hidden") : t("ใช้พรอมต์ซ้ำ", "Reuse Prompt")}
                                                        >
                                                            <Repeat className="h-4 w-4" />
                                                        </Button>
                                                    )}

                                                    {canCheckStatus && (
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-primary" onClick={() => handleCheckStatus(log.id)} disabled={checkingStatus === log.id} title={isFailed ? t("ตรวจสอบและขอคืนเครดิต", "Recheck & Refund") : t("เช็กสถานะ", "Check Status")}>
                                                            <RefreshCw className={`h-4 w-4 ${checkingStatus === log.id ? "animate-spin" : ""}`} />
                                                        </Button>
                                                    )}

                                                    {isCompleted && log.service_type !== "watermark" && (() => {
                                                        const downloadItems = getDownloadItems(log);
                                                        if (downloadItems.length === 0) return null;

                                                        const isVideo = log.service_type === "video" || log.service_type === "video_motion";

                                                        return (
                                                            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                                                {isVideo && (
                                                                    <>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 text-muted-foreground hover:text-primary"
                                                                            onClick={() => copyVideoUrl(log)}
                                                                            title={t("คัดลอก URL", "Copy URL")}
                                                                        >
                                                                            <Copy className="h-4 w-4" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="h-8 w-8 text-muted-foreground hover:text-amber-500"
                                                                            onClick={() => goToWatermark(log)}
                                                                            title={t("สร้างลายน้ำ", "Add Watermark")}
                                                                        >
                                                                            <Stamp className="h-4 w-4" />
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                {downloadItems.map((item, idx) => (
                                                                    <Button
                                                                        key={idx}
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-muted-foreground hover:text-primary relative group/btn"
                                                                        onClick={() => window.open(item.url, "_blank")}
                                                                        title={t(`เปิดไฟล์ ${idx + 1}`, `Open File ${idx + 1}`)}
                                                                    >
                                                                        <ExternalLink className="h-4 w-4" />
                                                                        {downloadItems.length > 1 && (
                                                                            <span className="absolute -top-1 -right-1 bg-primary text-[8px] text-primary-foreground w-3 h-3 flex items-center justify-center rounded-full">
                                                                                {idx + 1}
                                                                            </span>
                                                                        )}
                                                                    </Button>
                                                                ))}
                                                            </div>
                                                        )
                                                    })()}

                                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10" onClick={() => handleDelete(log.id)}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card>
                                    );
                                }

                                return (
                                    <Card
                                        key={log.id}
                                        variant="glass"
                                        className="group border-white/10 hover:border-primary/30 transition-all overflow-hidden flex flex-col h-full animate-in fade-in zoom-in duration-300"
                                    >
                                        <div className={`relative ${log.service_type === "video" || log.service_type === "video_motion" ? "w-full" : log.service_type === "music" ? "w-full" : "aspect-video"} bg-muted/30 overflow-hidden`}>
                                            {log.is_professional && (
                                                <div className="absolute top-2 left-2 z-10">
                                                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold shadow-lg">
                                                        <Crown className="h-2.5 w-2.5" />
                                                        Pro
                                                    </div>
                                                </div>
                                            )}
                                            {hasResult && !isPending ? (
                                                log.service_type === "photo" ? (
                                                    <img
                                                        src={log.result_url}
                                                        alt={getDisplayPrompt(log)}
                                                        className="w-full h-full object-contain transition-transform group-hover:scale-105 duration-700 cursor-pointer"
                                                        onClick={() => setSelectedImage(log.result_url)}
                                                    />
                                                ) : log.service_type === "music" ? (() => {
                                                    // Try to parse JSON for music with cover images and tracks
                                                    let coverImage: string | null = null;
                                                    let tracks: { audio: string; title?: string }[] = [];
                                                    try {
                                                        const parsed = JSON.parse(log.result_url || "");
                                                        if (parsed.tracks) {
                                                            tracks = parsed.tracks;
                                                            if (parsed.tracks[0]?.image) {
                                                                coverImage = parsed.tracks[0].image;
                                                            }
                                                        }
                                                    } catch {
                                                        // Fallback to comma-separated URLs
                                                        tracks = (log.result_url || "").split(',')
                                                            .filter(u => u.startsWith('http'))
                                                            .map(u => ({ audio: u }));
                                                    }

                                                    // Helper to fix Kie.ai music URLs
                                                    const getMusicUrl = (url: string) => {
                                                        if (url.includes('musicfile.kie.ai') && !url.includes('.mp3')) {
                                                            return url + '.mp3';
                                                        }
                                                        return url;
                                                    };

                                                    return (
                                                        <div className="w-full h-full flex flex-col">
                                                            {/* Cover Image or Placeholder */}
                                                            <div className="min-h-[100px] max-h-[140px] relative overflow-hidden">
                                                                {coverImage ? (
                                                                    <img
                                                                        src={coverImage}
                                                                        alt={getDisplayPrompt(log)}
                                                                        className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105 duration-700"
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-magenta-500/10 to-purple-500/10">
                                                                        <Music className="h-10 w-10 text-magenta-500" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {/* Audio Players */}
                                                            <div className="p-3 space-y-2 bg-muted/50">
                                                                {tracks.slice(0, 2).map((track, idx) => (
                                                                    <div key={idx} className="space-y-1">
                                                                        {tracks.length > 1 && (
                                                                            <p className="text-[10px] text-muted-foreground truncate">
                                                                                {track.title || `Track ${idx + 1}`}
                                                                            </p>
                                                                        )}
                                                                        <audio
                                                                            src={getMusicUrl(track.audio)}
                                                                            controls
                                                                            className="w-full h-8"
                                                                            preload="metadata"
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })() : (log.service_type === "video" || log.service_type === "video_motion") ? (() => {
                                                    let videoUrl = log.result_url;
                                                    // Try simple parsing
                                                    if (videoUrl?.startsWith('{')) {
                                                        try {
                                                            const parsed = JSON.parse(videoUrl);
                                                            videoUrl = parsed.resultUrls?.[0] || parsed.url || videoUrl;
                                                        } catch { }
                                                    } else if (videoUrl?.includes(',')) {
                                                        videoUrl = videoUrl.split(',')[0];
                                                    }

                                                    return (
                                                        <video
                                                            src={videoUrl || ""}
                                                            className="w-full h-auto block bg-black"
                                                            controls
                                                            preload="metadata"
                                                            playsInline
                                                        />
                                                    )
                                                })() : (
                                                    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-primary/10 to-purple-500/10">
                                                        <Video className="h-10 w-10 text-primary" />
                                                        <p className="text-[10px] uppercase tracking-widest font-bold opacity-50">{log.service_type}</p>
                                                    </div>
                                                )
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center flex-col gap-2">
                                                    {log.status === "failed" ? (
                                                        <AlertCircle className="h-10 w-10 text-destructive" />
                                                    ) : (
                                                        <Clock className="h-10 w-10 text-muted-foreground animate-pulse" />
                                                    )}
                                                    <p className="text-xs text-muted-foreground">
                                                        {log.status === "failed" ? t("ผิดพลาด", "Failed") : t("กำลังสร้าง...", "Generating...")}
                                                    </p>
                                                    {canCheckStatus && (
                                                        <Button
                                                            variant="glow"
                                                            size="sm"
                                                            onClick={() => handleCheckStatus(log.id)}
                                                            disabled={checkingStatus === log.id}
                                                            className="mt-2"
                                                        >
                                                            {checkingStatus === log.id ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <RefreshCw className="h-4 w-4 mr-2" />
                                                                    {log.status === "failed" ? t("ตรวจสอบและขอคืนเครดิต", "Recheck & Refund") : t("เช็กสถานะ", "Check Status")}
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Hover Overlay - Simplified (Reuse button moved to footer) */}
                                            {isCompleted && (() => {
                                                const downloadItems = getDownloadItems(log);
                                                if (downloadItems.length === 0) return null;

                                                if (log.service_type === "video" || log.service_type === "video_motion") {
                                                    return (
                                                        <div className="absolute top-2 right-2 flex flex-col gap-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                            {downloadItems.map((item, idx) => (
                                                                <div key={idx} className="flex flex-col gap-2 pointer-events-auto">
                                                                    <Button
                                                                        size="icon"
                                                                        variant="glass"
                                                                        className="rounded-full w-8 h-8 bg-black/60 hover:bg-primary border-0 shadow-lg backdrop-blur-sm"
                                                                        onClick={(e) => { e.stopPropagation(); copyVideoUrl(log); }}
                                                                        title={t("คัดลอก URL", "Copy URL")}
                                                                    >
                                                                        <Copy className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="glass"
                                                                        className="rounded-full w-8 h-8 bg-black/60 hover:bg-amber-500 border-0 shadow-lg backdrop-blur-sm"
                                                                        onClick={(e) => { e.stopPropagation(); goToWatermark(log); }}
                                                                        title={t("สร้างลายน้ำ", "Add Watermark")}
                                                                    >
                                                                        <Stamp className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="glass"
                                                                        className="rounded-full w-8 h-8 bg-black/60 hover:bg-primary border-0 shadow-lg backdrop-blur-sm"
                                                                        onClick={(e) => { e.stopPropagation(); window.open(item.url, "_blank"); }}
                                                                        title={t("เปิดลิงก์", "Open Link")}
                                                                    >
                                                                        <ExternalLink className="h-4 w-4" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="glass"
                                                                        className="rounded-full w-8 h-8 bg-black/60 hover:bg-primary border-0 shadow-lg backdrop-blur-sm"
                                                                        onClick={(e) => { e.stopPropagation(); handleDownloadFile(item.url, item.filename); }}
                                                                        title={t("ดาวน์โหลด", "Download")}
                                                                    >
                                                                        <Download className="h-4 w-4" />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    );
                                                }

                                                // Music or Watermark: No overlay needed
                                                if (log.service_type === "music" || log.service_type === "watermark") {
                                                    return null;
                                                }

                                                // Default overlay for photo
                                                return (
                                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4 z-10 pointer-events-none">
                                                        <div className="flex items-center justify-center gap-2 flex-wrap pointer-events-auto">
                                                            {downloadItems.map((item, idx) => (
                                                                <div key={idx} className="flex gap-2">
                                                                    <Button
                                                                        size="icon"
                                                                        variant="glow"
                                                                        className="rounded-full shadow-xl w-8 h-8"
                                                                        onClick={(e) => { e.stopPropagation(); setSelectedImage(item.url); }}
                                                                        title={t("ดูเต็มจอ", "View Fullscreen")}
                                                                    >
                                                                        <Maximize2 className="h-3 w-3" />
                                                                    </Button>
                                                                    <Button
                                                                        size="icon"
                                                                        variant="glow"
                                                                        className="rounded-full shadow-xl w-8 h-8"
                                                                        onClick={(e) => { e.stopPropagation(); handleDownloadFile(item.url, item.filename); }}
                                                                        title={t("ดาวน์โหลด", "Download")}
                                                                    >
                                                                        <Download className="h-3 w-3" />
                                                                    </Button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        <CardHeader className="p-5 pb-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                                    <span className="p-1 rounded bg-primary/10 text-primary">
                                                        {getServiceIcon(log.service_type)}
                                                    </span>
                                                    <span>{log.model}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {isNew && (
                                                        <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px] h-5">
                                                            New
                                                        </Badge>
                                                    )}
                                                    {getStatusBadge(log.status, log)}
                                                </div>
                                            </div>
                                            <CardDescription className="line-clamp-2 text-foreground/80 font-medium min-h-[40px]">
                                                {getDisplayPrompt(log)}
                                            </CardDescription>
                                            {/* Error Message for Failed Jobs */}
                                            {log.status === 'failed' && log.error_message && (
                                                <div className="flex items-start gap-2 mt-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20" title={log.error_message}>
                                                    <AlertTriangle className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
                                                    <p className="text-[11px] text-red-400 line-clamp-2">
                                                        {translateErrorMessage(log.error_message, t("th", "en") === "th")}
                                                    </p>
                                                </div>
                                            )}
                                        </CardHeader>

                                        <CardContent className="p-5 pt-0 mt-auto">
                                            <div className="flex items-center justify-between mt-4">
                                                <div className="flex flex-col gap-1">
                                                    <div className="flex items-center gap-3">
                                                        <div>
                                                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เมื่อวันที่", "Created At")}</span>
                                                            <span className="text-xs font-semibold block">{formatDate(log.created_at)}</span>
                                                        </div>
                                                        <div className="border-l border-white/10 pl-3">
                                                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter">{t("เครดิต", "Credits")}</span>
                                                            {log.status === "failed" ? (
                                                                log.refunded_at ? (
                                                                    <span className="text-xs font-semibold text-green-500 block">0 ({t("คืนแล้ว", "Refunded")})</span>
                                                                ) : (
                                                                    <span className="text-xs font-semibold text-red-400 block">-{log.credits_used} ({t("ยังไม่ได้คืน", "Not Refunded")})</span>
                                                                )
                                                            ) : (
                                                                <span className="text-xs font-semibold text-primary block">-{log.credits_used}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {log.service_type !== "watermark" && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-muted-foreground hover:text-primary rounded-lg transition-colors"
                                                            onClick={(e) => { e.stopPropagation(); handleReuse(log); }}
                                                            disabled={log.is_magic}
                                                            title={log.is_magic ? t("พรอมต์ถูกซ่อน", "Prompt hidden") : t("ใช้พรอมต์ซ้ำ", "Reuse Prompt")}
                                                        >
                                                            <Repeat className="h-4 w-4" />
                                                        </Button>
                                                    )}

                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDelete(log.id);
                                                        }}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                    {isCompleted && log.service_type !== "watermark" && (() => {
                                                        const downloadItems = getDownloadItems(log);
                                                        if (downloadItems.length === 0) return null;

                                                        return (
                                                            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                                                                {downloadItems.map((item, idx) => (
                                                                    <Button
                                                                        key={idx}
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-primary hover:bg-primary/10 rounded-lg transition-colors relative"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleDownloadFile(item.url, item.filename);
                                                                        }}
                                                                        title={downloadItems.length > 1 ? t(`ดาวน์โหลดไฟล์ ${idx + 1}`, `Download File ${idx + 1}`) : t("ดาวน์โหลด", "Download")}
                                                                    >
                                                                        <Download className="h-4 w-4" />
                                                                        {downloadItems.length > 1 && (
                                                                            <span className="absolute -top-1 -right-1 bg-primary text-[8px] text-primary-foreground w-3 h-3 flex items-center justify-center rounded-full">
                                                                                {idx + 1}
                                                                            </span>
                                                                        )}
                                                                    </Button>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-8">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1 || isLoading}
                                className="rounded-xl"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>

                            <div className="flex items-center gap-1 mx-2">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <Button
                                        key={page}
                                        variant={currentPage === page ? "default" : "ghost"}
                                        size="sm"
                                        onClick={() => setCurrentPage(page)}
                                        disabled={isLoading}
                                        className={`w-8 h-8 rounded-lg ${currentPage === page ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-white/10"}`}
                                    >
                                        {page}
                                    </Button>
                                ))}
                            </div>

                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages || isLoading}
                                className="rounded-xl"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                </div>
            </main>

            {/* Fullscreen Image Preview */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 animate-in fade-in duration-200 backdrop-blur-sm"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center group/modal">
                        <img
                            src={selectedImage}
                            alt="Full screen preview"
                            className="max-h-[92vh] max-w-full object-contain rounded-lg shadow-2xl"
                        />
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSelectedImage(null)}
                            className="absolute -top-12 right-0 md:top-4 md:right-4 text-white hover:bg-white/20 rounded-full h-10 w-10 bg-black/50"
                        >
                            <X className="h-6 w-6" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Error Details Dialog */}
            <Dialog open={!!selectedErrorLog} onOpenChange={(open) => !open && setSelectedErrorLog(null)}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-500">
                            <AlertTriangle className="h-5 w-5" />
                            {t("สาเหตุที่ล้มเหลว", "Failure Reason")}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedErrorLog && (
                        <p className="text-sm text-red-400">
                            {selectedErrorLog.error_message
                                ? translateErrorMessage(selectedErrorLog.error_message, t("th", "en") === "th")
                                : t("ไม่ทราบสาเหตุ - กรุณาติดต่อแอดมิน", "Unknown reason - please contact admin")}
                        </p>
                    )}
                </DialogContent>
            </Dialog>

            <Footer />
        </div>
    );
};

export default History;

